import React, { PropsWithChildren, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import PopupComponent from "../components/PopupComponent";
import { updateLeadStatus } from "../services/leadService";
import { LeadDataI } from "../views/leads/leads";
import { Logger } from '../utils/logger';
import { updateSingleLead } from "../actions/actions";
import { useDispatch } from 'react-redux';

interface PropsT {
  showStatusPopup: boolean;
  toggleLeadStatus: () => void;
  onStatusChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedStatus: string;
  setShowStatusPopup: React.Dispatch<React.SetStateAction<boolean>>;
  from: string;
  id: string;
  leadList: string;
  onLeadUpdate?: (updatedLead: LeadDataI) => void;
  getLeads: (leadId: any) => void;
  setOpenWithHeader: React.Dispatch<React.SetStateAction<boolean>>;
  updateEditedRow?: (newData: any) => void;
  filterParams?: any; // NEW: For filter-based selection
  isFilterMode?: boolean; // NEW: Track if using filter mode
}

interface StatusItem {
  color: string;
  idDefault: boolean;
  name: string;
  value: string;
}

interface StatusI {
  status: StatusItem[];
}

const SelectLeadStatus: React.FC<PropsWithChildren<PropsT>> = ({
  showStatusPopup,
  toggleLeadStatus,
  onStatusChange,
  selectedStatus,
  setShowStatusPopup,
  from,
  id,
  getLeads,
  setOpenWithHeader,
  leadList,
  updateEditedRow,
  onLeadUpdate,
  filterParams, // NEW: Destructure
  isFilterMode = false // NEW: Destructure
}) => {
  const [leadStatus, setLeadStatus] = useState<StatusI>();
  const [localSelectedStatus, setLocalSelectedStatus] = useState(selectedStatus);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (showStatusPopup) {
      setLocalSelectedStatus(selectedStatus);
    }
  }, [showStatusPopup, selectedStatus]);

  const getPreferncesFromLocalStorage = async () => {
    const preferences = localStorage.getItem("user_preferences");
    if (preferences) {
      try {
        const parsedPreferences = JSON.parse(preferences);
        setLeadStatus(parsedPreferences);
      } catch (error) {
        console.error("Error parsing user preferences:", error);
      }
    }
  };

  useEffect(() => {
    getPreferncesFromLocalStorage();
  }, []);

  const handleStatusChange = (statusValue: string) => {
    const syntheticEvent = {
      target: {
        value: statusValue
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    setLocalSelectedStatus(statusValue);
    onStatusChange(syntheticEvent);
  };

  const renderStatus = () =>
    leadStatus?.status?.map((item, i) => (
      <div 
        className={`status-option ${item.value === localSelectedStatus ? 'selected' : ''}`} 
        key={i}
        onClick={() => handleStatusChange(item.value)}
        role="button"
        tabIndex={0}
      >
        <input
          type="radio"
          checked={item.value === localSelectedStatus}
          value={item.value}
          id={`status-${item.value}`}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="status-radio"
        />
        <label 
          htmlFor={`status-${item.value}`} 
          className="status-label"
          onClick={(e) => e.preventDefault()}
        >
          <span 
            className="status-dot"
            style={{ backgroundColor: item.color }}
          />
          {item?.name}
        </label>
      </div>
    ));

  const onSave = async () => {
    let payload: any = {
      status: localSelectedStatus,
    };

    if (isFilterMode && filterParams) {
      // Filter mode: Send filterParams
      const { paginationParams, ...filterParamsWithoutPagination } = filterParams;
      payload.filterParams = filterParamsWithoutPagination;

      Logger.log('🎨 Status Update with filterParams:', {
        status: localSelectedStatus,
        filterParams: filterParamsWithoutPagination,
        isFilterMode
      });
    } else {
      // Manual mode: Send leadIDs (existing behavior)
      const leadIds = Array.isArray(id) ? id : [id];
      payload.leadIDs = leadIds;

      Logger.log('🎨 Status Update with manual selection:', {
        status: localSelectedStatus,
        leadIds,
        leadList
      });
    }

    try {
      const response = await updateLeadStatus(payload);

      Logger.log('📨 Status Update API Response:', {
        status: response?.status,
        hasData: !!response?.data,
        message: response?.data?.message
      });

      if (response && response.status) {
        if (!isFilterMode) {
          // Only dispatch to Redux for manual mode (single lead updates)
          Logger.log('✅ Status update successful, dispatching to Redux');
          dispatch(updateSingleLead(response.data.data));

          if (onLeadUpdate) {
            onLeadUpdate(response.data.data);
          } else if (updateEditedRow) {
            updateEditedRow(response?.data.data);
          }
        }

        toast.success(response?.data?.message || 'Status updated successfully');

        // Refresh leads list
        if (getLeads) {
          getLeads(null);
        }
      } else {
        Logger.error('❌ Status update failed - invalid response:', response);
        toast.error("Failed to update lead status");
      }
    } catch (err) {
      Logger.error('❌ Status update API error:', err);
      toast.error("Error updating lead status");
    } finally {
      setIsLoading(false);
      setShowStatusPopup(false);
    }
  };

  const handleClose = () => {
    setLocalSelectedStatus(selectedStatus);
    toggleLeadStatus();
  };

  const footerContent = (
    <>
      <button
        onClick={handleClose}
        type="button"
        className="btn-cancel"
        disabled={isLoading}
      >
        Cancel
      </button>
      <button
        disabled={!localSelectedStatus || isLoading}
        onClick={onSave}
        type="button"
        className="btn-save"
      >
        {isLoading ? 'Saving...' : 'Save'}
      </button>
    </>
  );

  return (
    <>
      <PopupComponent
        isOpen={showStatusPopup}
        onClose={handleClose}
        title="Select Status"
        size="sm"
        footer={footerContent}
      >
        <div className="status-list">
          {renderStatus()}
        </div>
      </PopupComponent>
      
      <Toaster position="top-right" reverseOrder={false} />
      
      <style>{`
        .status-list {
          padding: 0.25rem;
        }

        .status-option {
          display: flex;
          align-items: center;
          padding: 0.625rem;
          margin-bottom: 0.25rem;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.15s ease;
          border: 1px solid transparent;
          user-select: none;
        }

        .status-option:hover {
          background-color: #f8f9fa;
          border-color: #e9ecef;
        }

        .status-option.selected {
          background-color: #f0f7ff;
          border-color: #0d6efd;
        }

        .status-radio {
          margin-right: 10px;
          cursor: pointer;
          pointer-events: none;
        }

        .status-label {
          display: flex;
          align-items: center;
          margin: 0;
          cursor: pointer;
          flex-grow: 1;
          font-size: 0.9375rem;
          pointer-events: none;
        }

        .status-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          margin-right: 8px;
          display: inline-block;
        }

        .btn-cancel, .btn-save {
          padding: 0.375rem 0.75rem;
          border-radius: 4px;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.15s ease;
        }

        .btn-cancel {
          border: 1px solid #dee2e6;
          background-color: #fff;
          color: #6c757d;
          margin-right: 0.5rem;
        }

        .btn-cancel:hover:not(:disabled) {
          background-color: #f8f9fa;
          border-color: #c1c9d0;
        }

        .btn-save {
          border: none;
          background-color: #0d6efd;
          color: #fff;
        }

        .btn-save:hover:not(:disabled) {
          background-color: #0b5ed7;
        }

        .btn-save:disabled,
        .btn-cancel:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
};

export default SelectLeadStatus;